import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdmissionsChatbot } from '../hooks/useAdmissionsChatbot';
import { useAuth } from '../contexts/AuthContext';
import { Profile, supabase } from '../lib/supabase';
import { renderAdmissionsBotText } from '../lib/admissionsChatRender';
import './AdmissionsChatbot.css';
import hbsLogo from '../assets/HBS_LOGO.png';
import stanfordLogo from '../assets/STANFORD_LOGO.png';
import whartonLogo from '../assets/WHARTON_LOGO.png';
import columbiaLogo from '../assets/COLUMBIA_LOGO.png';
import mitLogo from '../assets/MIT_LOGO.png';
import chicagoBoothLogo from '../assets/CHICAGO_BOOTH_LOGO.png';
import kelloggLogo from '../assets/KELLOGG_LOGO.png';
import berkeleyHaasLogo from '../assets/BERKELEY_HAAS_LOGO.png';
import yaleSomLogo from '../assets/YALE_SOM_LOGO.png';
import dardenLogo from '../assets/DARDEN_LOGO.png';
import uclaAndersonLogo from '../assets/UCLA_ANDERSON_LOGO.png';
import emoryGoizuetaLogo from '../assets/EMORY_GOIZUETA_LOGO.png';

const AdmissionsChatbot: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const profileContext = useMemo(() => {
    if (!user || !profile) return '';
    const targetSchools = (profile.target_schools || []).join(', ') || 'Not provided';
    return [
      `User is logged in as: ${user.email || user.id}`,
      `University: ${profile.academic_institution || 'Not provided'}`,
      `Major/Minor: ${profile.major_minor || 'Not provided'}`,
      `GPA: ${profile.gpa ? `${profile.gpa}${profile.gpa_scale ? ` / ${profile.gpa_scale}` : ''}` : 'Not provided'}`,
      `GMAT: ${profile.gmat_score ? String(profile.gmat_score) : 'Not provided'}`,
      `GRE: ${profile.gre_score ? String(profile.gre_score) : 'Not provided'}`,
      `Target schools: ${targetSchools}`,
      `Work experience: ${profile.work_experience || 'Not provided'}`,
      `Extracurriculars: ${profile.extracurriculars || 'Not provided'}`,
      `Desired outcomes: ${profile.desired_outcomes || 'Not provided'}`,
      `Why MBA: ${profile.why_mba || 'Not provided'}`
    ].join('\n');
  }, [profile, user]);

  const {
    messages,
    inputText,
    setInputText,
    isLoading,
    handleSubmit,
    handleKeyPress,
    messagesEndRef
  } = useAdmissionsChatbot({ profileContext });

  const SCHOOL_LOGO_BY_NAME: Record<string, string> = {
    'Harvard Business School': hbsLogo,
    'Stanford Graduate School of Business': stanfordLogo,
    'Wharton School': whartonLogo,
    'Columbia Business School': columbiaLogo,
    'MIT Sloan': mitLogo,
    'Chicago Booth': chicagoBoothLogo,
    'Kellogg SOM': kelloggLogo,
    'Berkeley Haas': berkeleyHaasLogo,
    'Emory Goizueta Business School': emoryGoizuetaLogo,
    'UCLA Anderson School of Management': uclaAndersonLogo,
    'Darden School of Business': dardenLogo,
    'Yale SOM': yaleSomLogo
  };

  useEffect(() => {
    let cancelled = false;
    async function loadProfile() {
      if (!user?.id) {
        setProfile(null);
        return;
      }
      setIsProfileLoading(true);
      const { data } = await supabase.from('profiles').select('*').eq('user_id', user.id).maybeSingle();
      if (cancelled) return;
      setProfile((data as Profile | null) ?? null);
      setIsProfileLoading(false);
    }
    void loadProfile();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const profileStatRows = useMemo(
    () => [
      { label: 'University', value: profile?.academic_institution || 'Not provided' },
      { label: 'Major / Minor', value: profile?.major_minor || 'Not provided' },
      { label: 'GPA', value: profile?.gpa ? `${profile.gpa}${profile?.gpa_scale ? ` / ${profile.gpa_scale}` : ''}` : 'Not provided' },
      { label: 'GMAT', value: profile?.gmat_score ? String(profile.gmat_score) : 'Not provided' },
      { label: 'GRE', value: profile?.gre_score ? String(profile.gre_score) : 'Not provided' }
    ],
    [profile]
  );

  return (
    <div className="chatbot-page">
      <div className="chatbot-container">
        <div className="chatbot-header">
          <h1 className="chatbot-title">Admissions Assistant</h1>
          <p className="chatbot-subtitle">Get personalized guidance for your MBA application journey</p>
        </div>

        <div className="chatbot-layout">
          <div className="chatbot-main">
            <div className="chatbot-messages">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`message ${message.isUser ? 'user-message' : 'bot-message'}`}
                >
                  <div className="message-content">
                    <div className="message-text">
                      {message.isUser ? message.text : renderAdmissionsBotText(message.text)}
                    </div>
                    <div className="message-time">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="message bot-message">
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <form className="chatbot-input-form" onSubmit={handleSubmit}>
              <div className="input-container">
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Ask me anything about MBA admissions..."
                  className="message-input"
                  rows={1}
                  disabled={isLoading}
                />
                <div className="input-actions">
                  <button
                    type="submit"
                    className="send-button"
                    disabled={!inputText.trim() || isLoading}
                    title="Send message"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path d="M2 21L23 12L2 3V10L17 12L2 14V21Z" fill="currentColor" />
                    </svg>
                  </button>
                </div>
              </div>
            </form>
          </div>

          <aside className="chatbot-profile-panel" aria-label="Your profile context">
            <h2 className="chatbot-profile-title">Your Profile Context</h2>
            <div className="chatbot-profile-panel-scroll">
              {isProfileLoading ? (
                <p className="chatbot-profile-empty">Loading your profile...</p>
              ) : !user ? (
                <p className="chatbot-profile-empty">Log in to load your personal profile context.</p>
              ) : !profile ? (
                <p className="chatbot-profile-empty">No profile found yet. Complete Profile Builder for tailored advice.</p>
              ) : (
                <>
                  <div className="chatbot-profile-section">
                    <div className="chatbot-profile-section-title">Target Schools</div>
                    <div className="chatbot-school-logos">
                      {(profile.target_schools || []).length === 0 ? (
                        <div className="chatbot-profile-empty inline">No target schools selected yet.</div>
                      ) : (
                        (profile.target_schools || []).map((school) => {
                          const logo = SCHOOL_LOGO_BY_NAME[school];
                          return (
                            <div key={school} className="chatbot-school-chip" title={school}>
                              {logo ? (
                                <img src={logo} alt="" className="chatbot-school-logo" />
                              ) : (
                                <span className="chatbot-school-fallback">{school.slice(0, 2).toUpperCase()}</span>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div className="chatbot-profile-section">
                    <div className="chatbot-profile-section-title">Background</div>
                    <div className="chatbot-profile-stats">
                      {profileStatRows.map((row) => (
                        <div key={row.label} className="chatbot-profile-stat">
                          <span className="chatbot-profile-stat-label">{row.label}</span>
                          <span className="chatbot-profile-stat-value">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            {user && !isProfileLoading && (
              <Link to="/profile-builder" className="chatbot-profile-edit-link">
                Edit Profile
              </Link>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default AdmissionsChatbot;
